import { Fragment, useContext, useEffect, useMemo, useState } from 'react';

import { PhoebeChatOrganizationScopeContext } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import { useDynamicWidgetPromptAction } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widgets_context';
import {
    TEMPLATE_APPROVAL_BANNER_CLASS,
    TEMPLATE_APPROVAL_DOT_CLASS,
    TEMPLATE_CARD_CLASS,
    TEMPLATE_OUTLINE_BUTTON_CLASS,
    TEMPLATE_PRIMARY_BUTTON_CLASS,
    TEMPLATE_STEP_NUMBER_ACTIVE_CLASS,
    TEMPLATE_STEP_NUMBER_CLASS,
    TEMPLATE_STEP_NUMBER_DONE_CLASS,
    TEMPLATE_STEP_NUMBER_IDLE_CLASS,
    TEMPLATE_TAG_POSITIVE_CLASS,
    TEMPLATE_VARIABLE_CHIP_CLASS,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/template_theme';
import {
    type ToolApprovalDecision,
    type ToolExecutionEntry,
} from '@apps/web/components/phoebe_chat_view/transcript_types';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { Spinner } from '@apps/web/components/ui/spinner';
import { useOutreachCaregiverSelection } from '@apps/web/context/outreach_caregiver_selection';
import {
    type CaregiverShiftIds,
    caregiverShiftIdsEqual,
} from '@apps/web/lib/caregiver_shift_selections';
import { resolveOutreachAutoAssignFirstResponder } from '@apps/web/lib/outreach_utils';
import { cn } from '@apps/web/lib/utils';
import { useJoinedOutreach } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const OUTREACH_START_TOOL_NAME = 'manage_outreach_start_with_contacts';

type WizardArguments = {
    readonly outreachId: string;
    readonly caregiverIds: ReadonlyArray<string>;
    readonly caregiverShiftIds: CaregiverShiftIds | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// The runtime stores caregiver_ids either as a real array or as a
// JSON-encoded string (observed in stored run events) — accept both.
function decodeIdList(value: unknown): ReadonlyArray<string> | null {
    let candidate = value;
    if (typeof candidate === 'string') {
        try {
            candidate = JSON.parse(candidate);
        } catch {
            return null;
        }
    }
    if (
        !Array.isArray(candidate) ||
        candidate.some((item) => typeof item !== 'string')
    ) {
        return null;
    }
    return candidate as ReadonlyArray<string>;
}

function decodeCaregiverShiftIds(value: unknown): CaregiverShiftIds | null {
    let candidate = value;
    if (typeof candidate === 'string') {
        try {
            candidate = JSON.parse(candidate);
        } catch {
            return null;
        }
    }
    if (!isRecord(candidate)) {
        return null;
    }
    const mapping: CaregiverShiftIds = {};
    for (const [caregiverId, shiftIds] of Object.entries(candidate)) {
        const decodedShiftIds = decodeIdList(shiftIds);
        if (decodedShiftIds === null) {
            return null;
        }
        mapping[caregiverId] = [...decodedShiftIds];
    }
    return mapping;
}

export function parseOutreachStartApprovalArguments(
    entry: ToolExecutionEntry,
): WizardArguments | null {
    if (entry.name !== OUTREACH_START_TOOL_NAME || !isRecord(entry.arguments)) {
        return null;
    }
    const outreachId = entry.arguments['outreach_id'];
    if (typeof outreachId !== 'string') {
        return null;
    }
    const caregiverShiftIds = decodeCaregiverShiftIds(
        entry.arguments['caregiver_shift_ids'],
    );
    // The tool schema only defines caregiver_shift_ids; older stored run
    // events carried a caregiver_ids array instead. Accept either so both
    // current commands and legacy pending approvals render the wizard.
    const caregiverIds =
        caregiverShiftIds !== null
            ? Object.keys(caregiverShiftIds)
            : decodeIdList(entry.arguments['caregiver_ids']);
    if (caregiverIds === null) {
        return null;
    }
    return { outreachId, caregiverIds, caregiverShiftIds };
}

const STEP_DEFINITIONS = [
    { label: 'Shift & options', heading: null },
    { label: 'Caregivers', heading: 'Confirm caregivers' },
    { label: 'Message', heading: 'Edit message' },
    { label: 'Review', heading: 'Review before sending' },
] as const;

const AVATAR_TONE_CLASSES = [
    'bg-amber-100 text-amber-800',
    'bg-sky-100 text-sky-800',
    'bg-emerald-100 text-emerald-800',
    'bg-violet-100 text-violet-800',
    'bg-rose-100 text-rose-800',
] as const;

const WEEKDAY_FORMAT: Intl.DateTimeFormatOptions = { weekday: 'long' };

function caregiverInitials(name: string): string {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');
}

function formatLocalTime(iso: string, timezone: string): string {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
        return iso;
    }
    return parsed.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timezone,
    });
}

function formatLocalWeekday(iso: string, timezone: string): string | null {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }
    return parsed.toLocaleDateString('en-US', {
        ...WEEKDAY_FORMAT,
        timeZone: timezone,
    });
}

function formatLocalMonthDay(iso: string, timezone: string): string | null {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }
    return parsed.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: timezone,
    });
}

function formatPayRate(payRateCents: number | null | undefined): string | null {
    if (typeof payRateCents !== 'number' || payRateCents <= 0) {
        return null;
    }
    return `$${(payRateCents / 100).toFixed(2)}/hr`;
}

// SMS segment estimate for the counter the template shows under the
// message editor: GSM-7 segments are 160 chars (153 when concatenated).
function estimateSmsSegments(length: number): number {
    if (length <= 160) {
        return 1;
    }
    return Math.ceil(length / 153);
}

// Render "{variable}" placeholders as the template's violet chips.
function MessageTemplateText({ template }: { readonly template: string }) {
    const parts = template.split(/(\{[a-z0-9_ ]+\})/gi);
    return (
        <Fragment>
            {parts.map((part, index) => {
                const match = /^\{([a-z0-9_ ]+)\}$/i.exec(part);
                if (match === null) {
                    return <Fragment key={index}>{part}</Fragment>;
                }
                return (
                    <span
                        key={index}
                        className={cn(
                            'mx-0.5 text-[0.92em] whitespace-nowrap',
                            TEMPLATE_VARIABLE_CHIP_CLASS,
                        )}
                    >
                        {(match[1] ?? '').replace(/_/g, ' ')}
                    </span>
                );
            })}
        </Fragment>
    );
}

function extractTemplateVariables(template: string): ReadonlyArray<string> {
    const names = new Set<string>();
    for (const match of template.matchAll(/\{([a-z0-9_ ]+)\}/gi)) {
        names.add((match[1] ?? '').replace(/_/g, ' '));
    }
    return [...names];
}

function StepHeading({ text }: { readonly text: string }) {
    return <h3 className="text-sm font-semibold text-[#002429]">{text}</h3>;
}

function SectionLabel({ text }: { readonly text: string }) {
    return (
        <p className="text-[11px] font-semibold tracking-[0.04em] text-[#829494] uppercase">
            {text}
        </p>
    );
}

/**
 * Four-step approval surface for starting an outreach, copied from the
 * design template catalog (T1 approval card in wizard mode). Every value
 * shown is read from the real outreach the agent proposed to start. An
 * unchanged selection approves the proposal as-is; a changed selection
 * approves with the selection as an arguments override, so the agent's
 * own tool executes exactly the caregiver set the user saw and narrates
 * its own start.
 */
export function OutreachStartApprovalWizard({
    entry,
    wizardArguments,
    isSubmitting,
    onDecision,
}: {
    readonly entry: ToolExecutionEntry;
    readonly wizardArguments: WizardArguments;
    readonly isSubmitting: boolean;
    readonly onDecision: (
        decision: ToolApprovalDecision,
        argumentsOverride?: Record<string, unknown>,
    ) => Promise<void> | void;
}) {
    // Read the chat scope optionally: the wizard only mounts inside the
    // chat transcript, but tests and headless surfaces may not provide the
    // app-level organization provider the strict hook falls back to.
    const organizationScope = useContext(PhoebeChatOrganizationScopeContext);
    const organizationId = organizationScope?.organizationId ?? null;
    const timezone =
        organizationScope?.timezone ??
        Intl.DateTimeFormat().resolvedOptions().timeZone;
    const promptAction = useDynamicWidgetPromptAction();
    const { selection: liveSelection } = useOutreachCaregiverSelection();
    const [currentStep, setCurrentStep] = useState(1);
    const [showAllCaregivers, setShowAllCaregivers] = useState(false);
    const [selectedCaregiverIds, setSelectedCaregiverIds] = useState<
        ReadonlySet<string>
    >(() => new Set(wizardArguments.caregiverIds));
    const [hasTouchedSelection, setHasTouchedSelection] = useState(false);

    const { data: outreach } = useJoinedOutreach(
        organizationId,
        wizardArguments.outreachId,
        { reason: 'outreach-start-approval-wizard' },
    );

    const shifts = outreach?.shifts ?? [];
    const firstShift = shifts[0];
    const suggestedCaregivers = useMemo(() => {
        const suggestions = outreach?.suggested_caregivers ?? [];
        const proposed = new Set(wizardArguments.caregiverIds);
        // The approval covers the proposed set; keep those rows first so
        // the reviewer sees what they are approving before the alternates.
        return [...suggestions].sort(
            (left, right) =>
                Number(proposed.has(right.caregiver_id)) -
                Number(proposed.has(left.caregiver_id)),
        );
    }, [outreach?.suggested_caregivers, wizardArguments.caregiverIds]);
    const suggestedCaregiverShiftIds = useMemo<CaregiverShiftIds>(() => {
        const mapping: CaregiverShiftIds = {};
        for (const suggestion of suggestedCaregivers) {
            if (
                wizardArguments.caregiverIds.includes(
                    suggestion.caregiver_id,
                ) &&
                suggestion.covered_shift_ids.length > 0
            ) {
                mapping[suggestion.caregiver_id] = [
                    ...suggestion.covered_shift_ids,
                ];
            }
        }
        return mapping;
    }, [suggestedCaregivers, wizardArguments.caregiverIds]);
    const originalCaregiverShiftIds = useMemo(
        () => wizardArguments.caregiverShiftIds ?? null,
        [wizardArguments.caregiverShiftIds],
    );
    const fallbackCaregiverShiftIds = useMemo(
        () => originalCaregiverShiftIds ?? suggestedCaregiverShiftIds,
        [originalCaregiverShiftIds, suggestedCaregiverShiftIds],
    );
    // An empty matching selection is meaningful — the coordinator cleared
    // the table — and must block starting rather than fall back to the
    // proposal. Only a selection for a different outreach (or none) is
    // treated as absent.
    const liveSelectionForOutreach = useMemo(
        () =>
            liveSelection?.outreachId === wizardArguments.outreachId
                ? liveSelection.caregiverShiftIds
                : null,
        [liveSelection, wizardArguments.outreachId],
    );
    const effectiveCaregiverShiftIds = useMemo(
        () => liveSelectionForOutreach ?? fallbackCaregiverShiftIds,
        [fallbackCaregiverShiftIds, liveSelectionForOutreach],
    );
    useEffect(() => {
        if (hasTouchedSelection) {
            return;
        }
        const effectiveCaregiverIds = Object.keys(effectiveCaregiverShiftIds);
        setSelectedCaregiverIds((previous) => {
            if (
                previous.size === effectiveCaregiverIds.length &&
                effectiveCaregiverIds.every((caregiverId) =>
                    previous.has(caregiverId),
                )
            ) {
                return previous;
            }
            return new Set(effectiveCaregiverIds);
        });
    }, [effectiveCaregiverShiftIds, hasTouchedSelection]);

    const messageTemplate = useMemo(() => {
        for (const step of outreach?.steps ?? []) {
            const template =
                step.message_template ??
                step.callout_template ??
                step.callout_multi_shift_template ??
                step.group_template;
            if (template) {
                return template;
            }
        }
        return null;
    }, [outreach?.steps]);

    const acceptPolicyLabel = resolveOutreachAutoAssignFirstResponder(outreach)
        ? 'Assign automatically'
        : 'Hold for my review';

    const weekday = firstShift
        ? formatLocalWeekday(firstShift.shift_start_time, timezone)
        : null;
    const recurring = shifts.length > 1;
    const timeRange = firstShift
        ? `${formatLocalTime(firstShift.shift_start_time, timezone)} – ${formatLocalTime(
              firstShift.shift_end_time,
              timezone,
          )}`
        : null;
    const payRateLabel = formatPayRate(firstShift?.pay_rate);
    const selectedCaregiverShiftIds = useMemo<CaregiverShiftIds>(() => {
        // A caregiver checked from the alternates list has no entry in the
        // effective mapping; fall back to their suggestion coverage so the
        // started outreach includes them (previously they were silently
        // dropped).
        const coverageBySuggestion = new Map(
            suggestedCaregivers.map((suggestion) => [
                suggestion.caregiver_id,
                suggestion.covered_shift_ids,
            ]),
        );
        const selected: CaregiverShiftIds = {};
        for (const caregiverId of selectedCaregiverIds) {
            const shiftIds =
                effectiveCaregiverShiftIds[caregiverId] ??
                coverageBySuggestion.get(caregiverId);
            if (shiftIds !== undefined && shiftIds.length > 0) {
                selected[caregiverId] = [...shiftIds];
            }
        }
        return selected;
    }, [effectiveCaregiverShiftIds, selectedCaregiverIds, suggestedCaregivers]);
    const selectionChanged = useMemo(() => {
        // Compare the checked id set first: a caregiver checked from the
        // alternates list has no entry in the effective mapping, so a
        // mapping-only comparison would silently ignore them.
        if (selectedCaregiverIds.size !== wizardArguments.caregiverIds.length) {
            return true;
        }
        if (
            wizardArguments.caregiverIds.some(
                (id) => !selectedCaregiverIds.has(id),
            )
        ) {
            return true;
        }
        // Same caregivers — a live table selection can still narrow which
        // shifts a caregiver is contacted about.
        return (
            originalCaregiverShiftIds !== null &&
            !caregiverShiftIdsEqual(
                selectedCaregiverShiftIds,
                originalCaregiverShiftIds,
            )
        );
    }, [
        originalCaregiverShiftIds,
        selectedCaregiverIds,
        selectedCaregiverShiftIds,
        wizardArguments.caregiverIds,
    ]);

    const reviewing = currentStep === 4;
    const visibleCaregivers = showAllCaregivers
        ? suggestedCaregivers
        : suggestedCaregivers.slice(0, 3);
    const hiddenCaregiverCount =
        suggestedCaregivers.length - visibleCaregivers.length;

    const startWithSelectedCaregivers = () => {
        if (Object.keys(selectedCaregiverShiftIds).length === 0) {
            return;
        }
        void onDecision('allow_once', {
            caregiver_shift_ids: selectedCaregiverShiftIds,
        });
    };

    const shiftPanel = (
        <div className="space-y-4">
            {reviewing ? (
                <SectionLabel text="Shift & options" />
            ) : (
                <StepHeading
                    text={`Start outreach — ${selectedCaregiverIds.size} caregiver${
                        selectedCaregiverIds.size === 1 ? '' : 's'
                    }`}
                />
            )}
            {firstShift ? (
                <div className="space-y-1 border-b border-[#E9F0F0] pb-4">
                    <div className="flex items-baseline justify-between gap-2">
                        <p className="text-[15px]">
                            <span className="font-semibold">
                                {recurring && weekday
                                    ? `${weekday}s`
                                    : (weekday ?? 'Shift')}
                            </span>
                            <span className="text-[#42595E]">
                                {' '}
                                · {timeRange}
                            </span>
                        </p>
                        {promptAction !== null ? (
                            <button
                                type="button"
                                className="text-foreground cursor-pointer text-sm font-medium underline underline-offset-4"
                                onClick={() =>
                                    void promptAction(
                                        'Edit the shift details on this outreach before I start it.',
                                    )
                                }
                            >
                                Edit
                            </button>
                        ) : null}
                    </div>
                    <p className="text-[13px] text-[#617578]">
                        <span className="text-foreground font-medium">
                            {firstShift.patient_name}
                        </span>
                        {firstShift.patient_city
                            ? ` · ${firstShift.patient_city}${
                                  firstShift.patient_state
                                      ? `, ${firstShift.patient_state}`
                                      : ''
                              }`
                            : ''}
                    </p>
                </div>
            ) : null}
            <div className="space-y-2">
                <p className="text-xs font-semibold text-[#617578]">
                    When to cover
                </p>
                <div className="flex flex-wrap gap-2">
                    {weekday && firstShift ? (
                        <Fragment>
                            <WhenToCoverPill
                                label={`Next ${weekday}${
                                    formatLocalMonthDay(
                                        firstShift.shift_start_time,
                                        timezone,
                                    )
                                        ? ` (${formatLocalMonthDay(
                                              firstShift.shift_start_time,
                                              timezone,
                                          )})`
                                        : ''
                                }`}
                                active={!recurring}
                                onSelect={
                                    recurring && promptAction !== null
                                        ? () =>
                                              void promptAction(
                                                  `Cover only the next ${weekday} on this outreach instead of all future ones.`,
                                              )
                                        : undefined
                                }
                            />
                            <WhenToCoverPill
                                label={`All future ${weekday}s`}
                                active={recurring}
                                onSelect={
                                    !recurring && promptAction !== null
                                        ? () =>
                                              void promptAction(
                                                  `Extend this outreach to cover all future ${weekday} shifts, not just the next one.`,
                                              )
                                        : undefined
                                }
                            />
                        </Fragment>
                    ) : null}
                    <WhenToCoverPill
                        label="Pick dates…"
                        active={false}
                        onSelect={
                            promptAction !== null
                                ? () =>
                                      void promptAction(
                                          'I want to pick specific dates for this outreach to cover — ask me which ones.',
                                      )
                                : undefined
                        }
                    />
                </div>
            </div>
            <div className="space-y-3">
                <p className="text-xs font-semibold text-[#617578]">
                    Outreach settings
                </p>
                {payRateLabel !== null ? (
                    <SettingRow label="Pay rate">
                        <span className="rounded-xl bg-[#F0F5F5] px-3.5 py-2 text-sm font-medium">
                            {payRateLabel}
                        </span>
                    </SettingRow>
                ) : null}
                <SettingRow label="When someone accepts">
                    <button
                        type="button"
                        className={cn(
                            'flex items-center gap-2 rounded-xl bg-[#F0F5F5] px-3.5 py-2 text-sm font-medium',
                            promptAction !== null
                                ? 'cursor-pointer'
                                : 'cursor-default',
                        )}
                        onClick={
                            promptAction !== null
                                ? () =>
                                      void promptAction(
                                          acceptPolicyLabel ===
                                              'Hold for my review'
                                              ? 'When a caregiver accepts this outreach, assign them automatically instead of holding for my review.'
                                              : 'When a caregiver accepts this outreach, hold the acceptance for my review instead of assigning automatically.',
                                      )
                                : undefined
                        }
                    >
                        {acceptPolicyLabel}
                        <span className="text-muted-foreground text-xs">⌄</span>
                    </button>
                </SettingRow>
            </div>
        </div>
    );

    const caregiversPanel = (
        <div className="space-y-1">
            {reviewing ? (
                <SectionLabel text="Caregivers" />
            ) : (
                <StepHeading text="Confirm caregivers" />
            )}
            {suggestedCaregivers.length === 0 ? (
                <p className="text-muted-foreground py-3 text-sm">
                    Caregiver recommendations are still being prepared.
                </p>
            ) : (
                <Fragment>
                    {visibleCaregivers.map((suggestion, index) => (
                        <div
                            key={suggestion.caregiver_id}
                            className={cn(
                                'flex items-center gap-2 py-2.5',
                                index > 0 && 'border-t border-[#E9F0F0]',
                            )}
                        >
                            <Checkbox
                                checked={selectedCaregiverIds.has(
                                    suggestion.caregiver_id,
                                )}
                                onCheckedChange={(checked) => {
                                    setHasTouchedSelection(true);
                                    setSelectedCaregiverIds((previous) => {
                                        const next = new Set(previous);
                                        if (checked === true) {
                                            next.add(suggestion.caregiver_id);
                                        } else {
                                            next.delete(
                                                suggestion.caregiver_id,
                                            );
                                        }
                                        return next;
                                    });
                                }}
                                aria-label={`Include ${suggestion.caregiver_name}`}
                            />
                            <span
                                aria-hidden="true"
                                className={cn(
                                    'flex size-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                                    AVATAR_TONE_CLASSES[
                                        index % AVATAR_TONE_CLASSES.length
                                    ],
                                )}
                            >
                                {caregiverInitials(suggestion.caregiver_name)}
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-[13px] font-medium text-[#002429]">
                                    {suggestion.caregiver_name}
                                </p>
                                <p className="truncate text-[11px] text-[#617578]">
                                    {suggestion.covered_shift_ids.length > 1
                                        ? `Covers ${suggestion.covered_shift_ids.length} shifts`
                                        : 'Covers this shift'}
                                </p>
                            </div>
                            <span
                                className={cn(
                                    TEMPLATE_TAG_POSITIVE_CLASS,
                                    'max-w-56 shrink-0 truncate',
                                )}
                                title={suggestion.reasoning}
                            >
                                {suggestion.reasoning}
                            </span>
                        </div>
                    ))}
                    {hiddenCaregiverCount > 0 ? (
                        <button
                            type="button"
                            className="w-full cursor-pointer border-t border-[#E9F0F0] py-2 text-left text-xs font-medium text-[#5533EE] transition-colors hover:text-[#4629C7]"
                            onClick={() => setShowAllCaregivers(true)}
                        >
                            + {hiddenCaregiverCount} more matched — show
                        </button>
                    ) : null}
                </Fragment>
            )}
        </div>
    );

    const messagePanel = (
        <div className="space-y-3">
            {reviewing ? (
                <SectionLabel text="Message" />
            ) : (
                <StepHeading text="Edit message" />
            )}
            {messageTemplate === null ? (
                <p className="text-muted-foreground py-3 text-sm">
                    This outreach has no SMS message step.
                </p>
            ) : (
                <Fragment>
                    <div className="space-y-2">
                        <p className="text-muted-foreground text-sm">
                            Message variables:
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {extractTemplateVariables(messageTemplate).map(
                                (variable) => (
                                    <span
                                        key={variable}
                                        className={cn(
                                            'px-2.5 py-1 text-sm',
                                            TEMPLATE_VARIABLE_CHIP_CLASS,
                                        )}
                                    >
                                        {variable}
                                    </span>
                                ),
                            )}
                        </div>
                    </div>
                    <div className="border-border rounded-xl border p-4 text-[15px] leading-7">
                        <MessageTemplateText template={messageTemplate} />
                    </div>
                    <div className="flex items-center justify-between gap-2">
                        <p className="text-right text-xs text-amber-700">
                            {messageTemplate.length} chars ·{' '}
                            {estimateSmsSegments(messageTemplate.length)} SMS
                            segment
                            {estimateSmsSegments(messageTemplate.length) === 1
                                ? ''
                                : 's'}
                        </p>
                        {promptAction !== null ? (
                            <button
                                type="button"
                                className="text-foreground cursor-pointer text-sm font-medium underline underline-offset-4"
                                onClick={() =>
                                    void promptAction(
                                        'Rewrite the outreach message before I start it — ask me what to change.',
                                    )
                                }
                            >
                                Edit with Phoebe
                            </button>
                        ) : null}
                    </div>
                </Fragment>
            )}
        </div>
    );

    const stepPanels = [shiftPanel, caregiversPanel, messagePanel];

    return (
        <div
            data-testid="outreach-approval-wizard"
            className={cn(
                'w-full max-w-2xl overflow-hidden',
                TEMPLATE_CARD_CLASS,
            )}
        >
            <div className={TEMPLATE_APPROVAL_BANNER_CLASS}>
                <span className={TEMPLATE_APPROVAL_DOT_CLASS} />
                Needs your approval
            </div>
            <div className="p-5">
                <div className="border-border/60 mb-5 flex items-center border-b pb-4">
                    {STEP_DEFINITIONS.map((definition, index) => {
                        const stepNumber = index + 1;
                        const isDone = stepNumber < currentStep;
                        const isActive = stepNumber === currentStep;
                        return (
                            <Fragment key={definition.label}>
                                {index > 0 ? (
                                    <span
                                        aria-hidden="true"
                                        className="bg-border mx-2 h-px min-w-6 flex-1"
                                    />
                                ) : null}
                                <button
                                    type="button"
                                    aria-current={isActive ? 'step' : undefined}
                                    disabled={stepNumber > currentStep}
                                    className={cn(
                                        'flex flex-col items-center gap-1',
                                        stepNumber <= currentStep
                                            ? 'cursor-pointer'
                                            : 'cursor-default',
                                    )}
                                    onClick={() => {
                                        if (stepNumber <= currentStep) {
                                            setCurrentStep(stepNumber);
                                        }
                                    }}
                                >
                                    <span
                                        className={cn(
                                            TEMPLATE_STEP_NUMBER_CLASS,
                                            isActive
                                                ? TEMPLATE_STEP_NUMBER_ACTIVE_CLASS
                                                : isDone
                                                  ? TEMPLATE_STEP_NUMBER_DONE_CLASS
                                                  : TEMPLATE_STEP_NUMBER_IDLE_CLASS,
                                        )}
                                    >
                                        {stepNumber}
                                    </span>
                                    <span
                                        className={cn(
                                            'text-[10px] font-medium tracking-[0.06em] uppercase',
                                            isActive
                                                ? 'text-[#42595E]'
                                                : 'text-[#829494]',
                                        )}
                                    >
                                        {definition.label}
                                    </span>
                                </button>
                            </Fragment>
                        );
                    })}
                </div>
                {reviewing ? (
                    <div className="space-y-3.5">
                        <StepHeading text="Review before sending" />
                        {stepPanels.map((panel, index) => (
                            <div
                                key={
                                    STEP_DEFINITIONS[index]?.label ??
                                    String(index)
                                }
                                className={cn(
                                    index > 0 &&
                                        'border-t border-[#E9F0F0] pt-3.5',
                                )}
                            >
                                {panel}
                            </div>
                        ))}
                    </div>
                ) : (
                    stepPanels[currentStep - 1]
                )}
                <div className="mt-6 flex items-center justify-between gap-2">
                    <button
                        type="button"
                        className={TEMPLATE_OUTLINE_BUTTON_CLASS}
                        disabled={currentStep === 1 || isSubmitting}
                        onClick={() => setCurrentStep(currentStep - 1)}
                    >
                        Back
                    </button>
                    {reviewing ? (
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                className={TEMPLATE_OUTLINE_BUTTON_CLASS}
                                disabled={isSubmitting}
                                onClick={() => void onDecision('deny')}
                            >
                                Cancel
                            </button>
                            {selectionChanged ? (
                                <button
                                    type="button"
                                    className={TEMPLATE_PRIMARY_BUTTON_CLASS}
                                    disabled={
                                        isSubmitting ||
                                        Object.keys(selectedCaregiverShiftIds)
                                            .length === 0
                                    }
                                    onClick={startWithSelectedCaregivers}
                                >
                                    {isSubmitting ? (
                                        <Spinner size="xs" />
                                    ) : null}
                                    Start{' '}
                                    {
                                        Object.keys(selectedCaregiverShiftIds)
                                            .length
                                    }{' '}
                                    selected
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className={TEMPLATE_PRIMARY_BUTTON_CLASS}
                                    disabled={isSubmitting}
                                    onClick={() =>
                                        void onDecision('allow_once')
                                    }
                                >
                                    {isSubmitting ? (
                                        <Spinner size="xs" />
                                    ) : null}
                                    Start outreach
                                </button>
                            )}
                        </div>
                    ) : (
                        <button
                            type="button"
                            className={TEMPLATE_PRIMARY_BUTTON_CLASS}
                            disabled={isSubmitting}
                            onClick={() => setCurrentStep(currentStep + 1)}
                        >
                            {currentStep === 3 ? 'Review' : 'Continue'}
                        </button>
                    )}
                </div>
                {reviewing && selectionChanged ? (
                    <p className="text-muted-foreground mt-2 text-right text-xs">
                        Starts your selected list (
                        {Object.keys(selectedCaregiverShiftIds).length}) instead
                        of Phoebe&rsquo;s proposed list (
                        {wizardArguments.caregiverIds.length}).
                    </p>
                ) : null}
                {entry.error ? (
                    <p className="mt-2 text-xs text-amber-700">{entry.error}</p>
                ) : null}
            </div>
        </div>
    );
}

function WhenToCoverPill({
    label,
    active,
    onSelect,
}: {
    readonly label: string;
    readonly active: boolean;
    readonly onSelect?: () => void;
}) {
    return (
        <button
            type="button"
            className={cn(
                'rounded-full border px-4 py-1.5 text-sm font-medium',
                active
                    ? 'border-transparent bg-[#0d2b2b] text-white dark:bg-teal-200 dark:text-teal-950'
                    : 'border-border text-foreground bg-background',
                onSelect ? 'cursor-pointer' : 'cursor-default',
            )}
            onClick={onSelect}
        >
            {label}
        </button>
    );
}

function SettingRow({
    label,
    children,
}: {
    readonly label: string;
    readonly children: React.ReactNode;
}) {
    return (
        <div className="flex items-center gap-4">
            <p className="text-foreground w-44 shrink-0 text-sm">{label}</p>
            {children}
        </div>
    );
}
